import { defineStore } from 'pinia';
import { CUSTOMER_SEGMENTS, Store, UPGRADE_CUSTOMER_PARAM_VALUE_MAP, URL_PARAM_MAP } from '@/setup/globals';
import { setInitialData } from '@/stores/plugins/initial-data';
import useSessionStore from '@/stores/session';
import api from '@/helpers/api';

export type StoreState = {
  entries: Record<string, unknown>[];
  course: Record<string, unknown> | null;
  recommendations: Record<string, unknown>[];
  bookingCourse: Record<string, unknown> | null;
  filters: Record<string, unknown>[];
  selectedFilters: Record<string, unknown>[];
  pagination: {
    totalAmount: number;
    currentPage: number;
    itemsPerPage: number;
  };
  runningRequests: {
    apiFetchEntries: boolean;
    apiFetchRecommendations: boolean;
    apiBookCourse: boolean;
    apiCourseWaitingList: boolean;
    apiSubmitQuestionnaire: boolean;
  };
};

const storeName = Store.Courses;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      entries: [],
      course: null,
      recommendations: [],
      bookingCourse: null,
      filters: [],
      selectedFilters: [],
      pagination: {
        totalAmount: 0,
        currentPage: 0,
        itemsPerPage: 12,
      },
      runningRequests: {
        apiFetchEntries: false,
        apiFetchRecommendations: false,
        apiBookCourse: false,
        apiCourseWaitingList: false,
        apiSubmitQuestionnaire: false,
      },
    };

    if (initialData?.entries?.length) {
      initialData.entries = initialData.entries.map((entry) => ({
        ...entry,
        page: initialData?.pagination?.currentPage || 1,
      }));
    }

    setInitialData(state, initialData);

    return state;
  },
  getters: {
    getBooking: (state) => state.course?.booking || null,
  },
  actions: {
    setPagination(pagination: Record<string, number>) {
      if (pagination?.totalAmount) {
        this.pagination.totalAmount = pagination.totalAmount;
      }

      if (pagination?.currentPage) {
        this.pagination.currentPage = pagination.currentPage;
      }

      if (pagination?.itemsPerPage) {
        this.pagination.itemsPerPage = pagination.itemsPerPage;
      }
    },

    pushEntries(entries: Record<string, unknown>[]) {
      if (entries?.length) {
        entries.forEach((entry) => {
          this.entries.push({ ...entry, page: this.pagination.currentPage });
        });
      }
    },

    /**
     * Fetches the course entries from the backend.
     */
    apiFetchEntries({ page, limit, filters, reset }) {
      this.runningRequests.apiFetchEntries = true;

      const url = api.getUrl('course-entries');

      return api
        .post(url, { page, limit, filters })
        .then((response) => {
          const { pagination, entries, filters: responseFilters } = response.data?.data || {};

          if (reset) {
            this.entries = [];
          }

          if (pagination) {
            this.setPagination(pagination);
          }

          this.pushEntries(entries);

          if (responseFilters) {
            // If a filter object has multiple values we need to check if the count is greater 0.
            // Otherwise, a helper property is defined to disable the property.
            responseFilters.forEach((filterItem) => {
              if (filterItem.values) {
                filterItem.values.forEach((value) => {
                  value.disabled = !value.count;
                });
              }
            });

            this.filters = responseFilters;
          }

          if (pagination || entries || filters) {
            return response;
          }

          throw new Error('API failure during "course/apiFetchEntries" request.');
        })
        .finally(() => {
          this.runningRequests.apiFetchEntries = false;
        });
    },

    /**
     * Fetches a list for recommended courses.
     */
    apiFetchRecommendations({ courseId }) {
      this.runningRequests.apiFetchRecommendations = true;

      const url = api.getUrl('course-recommendations', { courseId });

      return api
        .get(url)
        .then((response) => {
          const { data } = response.data;

          if (Array.isArray(data)) {
            this.recommendations = data;

            return data;
          }

          throw new TypeError('Invalid response for "course/apiFetchRecommendations".');
        })
        .finally(() => {
          this.runningRequests.apiFetchRecommendations = false;
        });
    },

    /**
     * Sends a booking request to the server for a given course and user.
     */
    apiBookCourse(urlParameters: Record<string, unknown>) {
      this.runningRequests.apiBookCourse = true;

      const url = api.getUrl('course-book', urlParameters);

      return api
        .post(url)
        .then((response) => {
          const { data } = response.data;

          if (data) {
            this.course = data;

            return data;
          }

          throw new Error('Invalid response for "course/apiBookCourse".');
        })
        .finally(() => {
          this.runningRequests.apiBookCourse = false;
        });
    },

    /**
     * Sends a waiting list subscription for the given course and user to the server.
     */
    apiCourseWaitingList(urlParameters: Record<string, unknown>) {
      this.runningRequests.apiCourseWaitingList = true;

      const url = api.getUrl('course-book', urlParameters);

      return api
        .post(url)
        .then((response) => {
          const { data } = response.data;

          if (data) {
            this.course = data;

            return data;
          }

          throw new Error('Invalid response for "course/apiCourseWaitingList".');
        })
        .finally(() => {
          this.runningRequests.apiCourseWaitingList = false;
        });
    },

    /**
     * Initialises a booking or waiting list subscription for the given course.
     */
    registerForCourse(course: Record<string, unknown>) {
      const sessionStore = useSessionStore();

      // If the user is not logged in the process is stopped and the CDC login window will open.
      // An url parameter is added to complete the booking after the redirect.
      if (!sessionStore.getIsLoggedIn) {
        const search = new URLSearchParams(window.location.search);

        search.set(URL_PARAM_MAP.continueCourseRegistration, '1');

        window.history.replaceState(null, null, `?${search.toString()}`);

        this._vm.$gtm.pushCourseLoginRequired();
        this._vm.$cdc.auth.openLogin();

        return Promise.reject("No user session while executing 'course/registerForCourse'.");
      }

      const activeUser = sessionStore.user;

      // If the user account type is basic and the course is not free, the user needs to be redirected
      // to the service landing page upgrade the account via CDC.
      if (activeUser?.segments?.includes(CUSTOMER_SEGMENTS.basic) && !course?.isFreeCourse) {
        const getUrls = sessionStore.urls;
        const searchParameters = new URLSearchParams();

        searchParameters.append(URL_PARAM_MAP.upgradeCustomer, UPGRADE_CUSTOMER_PARAM_VALUE_MAP.academy);
        searchParameters.append(URL_PARAM_MAP.referrerURL, window.location.href);

        window.location.href = `${getUrls.serviceLandingPage}?${searchParameters.toString()}`;

        return Promise.reject("Basic user on paid course. Service page redirect. 'course/registerForCourse'.");
      }

      // If the user has no licence number assigned the process is stopped and the CDC license number update window will open.
      // CDC will return a license number which will be stored and we complete the process.
      // eslint-disable-next-line no-underscore-dangle
      if (course?.cePointsAvailable && !activeUser?.licenseNumber && !course?._skipLicenseUpdate) {
        this._vm.$cdc.profile.onEnterCustomerLicenseNumber().then((licenseNumber) => {
          if (licenseNumber) {
            sessionStore.updateLicenseNumber(licenseNumber).then();
          }

          return this.registerForCourse({ ...course, _skipLicenseUpdate: true });
        });

        return Promise.reject(
          "License number missing on customer. Updating via CDC initialized 'course/registerForCourse'.",
        );
      }

      // eslint-disable-next-line no-underscore-dangle
      delete course._skipLicenseUpdate;

      if (course.availableSeats !== null && course?.availableSeats <= 0) {
        return this.apiCourseWaitingList({ courseId: course.id, customerId: activeUser?.uid }).then(() => {
          this.bookingCourse = course;
        });
      }

      this.bookingCourse = course;

      return Promise.resolve(course);
    },

    /**
     * Submits a completed questionnaire.
     */
    apiSubmitQuestionnaire(payload) {
      this.runningRequests.apiSubmitQuestionnaire = true;

      const url = api.getUrl('course-questionnaire', { courseId: payload.courseId });

      return api.post(url, payload).finally(() => {
        this.runningRequests.apiSubmitQuestionnaire = false;
      });
    },
  },
});
